import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { useDataTable } from '@ovComponents/2-component/dataTable';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { TextField } from '@ovComponents/2-component/textField/textField';
import type { Theme } from 'hephaestus/core';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from 'hephaestus/web';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from 'hephaestus/webIcons';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useFormatMoney } from '../hooks/useFormatMoney';
import { useTickerColumns } from '../hooks/useTickerColumns';
import {
  ALIGNED_TABLE_SX,
  BLOTTER_INPUT_SX,
  BOLD_SX,
  BORDER_RIGHT,
  LABEL_SX,
  MICRO_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SMALL_BOLD_SX,
  SMALL_SX,
  TABULAR_SX,
  TRUNCATE_SX,
  VALUE_SX,
} from '../styles';
import type { AggregateExposure, DisplayDensity } from '../types';
import {
  DENSITY_CONFIG,
  getAssetClassName,
  tradeSideColor,
  weightColor,
} from '../utils';
import { DIRECTION_CHIPS } from '../utils/constants';
import {
  PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS,
  PortfolioTradingTableHead,
} from './portfolioTradingTableHead';

const StatBox = ({
  label,
  value,
  valueColor,
  theme,
}: {
  label: string;
  value: React.ReactNode;
  valueColor?: string;
  theme: Theme;
}) => (
  <Box
    sx={{
      flex: 1,
      py: 0.75,
      px: 1.5,
      ...BORDER_RIGHT,
      '&:last-child': { borderRight: 'none' },
    }}
  >
    <Typography variant='labelSmall' component='div' sx={SECTION_HEADER_SX}>
      {label}
    </Typography>
    <Typography
      variant='bodySmall'
      component='div'
      sx={{ ...BOLD_SX, color: valueColor || theme.color.onSurface }}
    >
      {value}
    </Typography>
  </Box>
);

type PortfolioSelection = {
  ticker: string;
  portfolioId: string;
  portfolioReportId: string;
  portfolioName: string;
  subAccountId: string;
  financialProductId: string;
  currency: string;
  valueCents: number;
  quantity: number;
  expectedPercentage: number;
  expectedValueCents: number;
  portfolioMarketValueCents: number;
  cashAvailableForTradeCents: number;
  modelPortfolioType: string;
  objectId: string;
};

type CrossPortfolioExposureProps = {
  exposures: AggregateExposure[];
  searchTerm: string;
  density?: DisplayDensity;
  onCreateTrades?: (
    selections: PortfolioSelection[],
    direction: 'SELL' | 'BUY' | 'REBALANCE',
    buyAmountCentsPerAccount?: number,
  ) => void;
};

const ExposureRow = ({
  exposure,
  isSelected,
  onToggleTicker,
  theme,
}: {
  exposure: AggregateExposure;
  isSelected: boolean;
  onToggleTicker: (ticker: string) => void;
  densityCfg: (typeof DENSITY_CONFIG)[keyof typeof DENSITY_CONFIG];
  theme: Theme;
}) => {
  const formatMoney = useFormatMoney();
  const [expanded, setExpanded] = useState(false);
  const [showAllPortfolios, setShowAllPortfolios] = useState(false);
  const [sortCol, setSortCol] = useState<'value' | 'qty' | 'wt' | 'share'>(
    'value',
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };
  const pct = Math.abs(exposure.percentOfTotalAum);
  const concentrationColor = weightColor(theme.color, pct);

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          '&:hover': { bgcolor: theme.color.neutralHover },
          bgcolor: isSelected ? theme.color.primaryVariant : undefined,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckboxField
              checked={isSelected}
              onChange={() => onToggleTicker(exposure.ticker)}
              customStyle={{ transform: 'scale(0.8)' }}
            />
          </Box>
        </TableCell>
        <TableCell>
          <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
            <IconButton
              size='xsmall'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} fontSize='inherit' />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{ fontSize: 16 }}
                  fontSize='inherit'
                />
              )}
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ py: 0.5 }}>
          <Box sx={{ lineHeight: 1.3 }}>
            <Box
              component='span'
              sx={{ ...BOLD_SX, display: 'block', fontSize: 'inherit' }}
            >
              {exposure.ticker}
            </Box>
            <Box
              component='span'
              sx={{
                ...SECONDARY_TEXT_SX,
                ...TRUNCATE_SX,
                display: 'block',
                fontSize: '0.75rem',
              }}
            >
              {exposure.productName}
            </Box>
          </Box>
        </TableCell>
        <TableCell right number>
          {formatMoney(exposure.totalValueCents)}
        </TableCell>
        <TableCell right number>
          {exposure.totalQuantity.toFixed(2)}
        </TableCell>
        <TableCell center bold>
          {exposure.portfolioCount}
        </TableCell>
        <TableCell right>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              justifyContent: 'flex-end',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 6,
                bgcolor: theme.color.disabled,
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: `${Math.min(pct * 4, 100)}%`,
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: concentrationColor,
                  transition: 'width 0.3s',
                }}
              />
            </Box>
            <Box
              component='span'
              sx={{
                ...TABULAR_SX,
                minWidth: 40,
                textAlign: 'right',
                fontWeight: pct > 15 ? 700 : 400,
                color: concentrationColor,
              }}
            >
              {exposure.percentOfTotalAum.toFixed(1)}%
            </Box>
          </Box>
        </TableCell>
        <TableCell right>{exposure.currency}</TableCell>
      </TableRow>

      {expanded &&
        (() => {
          const sorted = [...exposure.portfolios].sort(
            (a, b) => b.valueCents - a.valueCents,
          );
          const withHoldings = sorted.filter((p) => p.valueCents > 0);
          const zeroCount = sorted.length - withHoldings.length;
          const displayPortfolios = showAllPortfolios ? sorted : withHoldings;
          const maxVal = sorted[0]?.valueCents || 1;
          const totalVal = exposure.totalValueCents || 1;
          const avgVal =
            sorted.length > 0
              ? sorted.reduce((s, p) => s + p.valueCents, 0) / sorted.length
              : 0;
          const avgWt =
            sorted.length > 0
              ? sorted.reduce((s, p) => s + p.percentOfPortfolio, 0) /
                sorted.length
              : 0;
          const maxWt =
            sorted.length > 0
              ? Math.max(...sorted.map((p) => p.percentOfPortfolio))
              : 0;
          const topShare =
            maxVal > 0 ? (sorted[0]?.valueCents / totalVal) * 100 : 0;
          const hasConcentration = topShare > 50;

          return (
            <TableRow>
              <TableCell colSpan={8} sx={{ py: 0, borderBottom: 'none' }}>
                <Box
                  sx={{
                    bgcolor: theme.color.surface,
                    borderTop: '2px solid',
                    borderColor: theme.color.primary,
                    borderBottom: '1px solid',
                    borderBottomColor: theme.color.outlineVariant,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0,
                      borderBottom: '1px solid',
                      borderColor: theme.color.outlineVariant,
                    }}
                  >
                    {[
                      {
                        label: 'Portfolios',
                        value:
                          zeroCount > 0
                            ? `${withHoldings.length} of ${exposure.portfolioCount} with holdings`
                            : `${exposure.portfolioCount}`,
                      },
                      {
                        label: 'Total Value',
                        value: formatMoney(exposure.totalValueCents),
                      },
                      { label: 'Avg Value', value: formatMoney(avgVal) },
                      {
                        label: 'Total Qty',
                        value: exposure.totalQuantity.toFixed(2),
                      },
                      { label: 'Avg Wt%', value: `${avgWt.toFixed(1)}%` },
                      {
                        label: 'Max Wt%',
                        value: `${maxWt.toFixed(1)}%`,
                        color: weightColor(theme.color, maxWt),
                      },
                      ...(hasConcentration
                        ? [
                            {
                              label: 'Concentration',
                              value: `${topShare.toFixed(1)}% in 1 portfolio`,
                              color: theme.color.warning,
                            },
                          ]
                        : []),
                    ].map((stat) => (
                      <StatBox
                        key={stat.label}
                        label={stat.label}
                        value={stat.value}
                        valueColor={stat.color}
                        theme={theme}
                      />
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'minmax(120px, 1.4fr) 100px 80px 60px 54px',
                      gap: 0,
                      px: 1,
                      py: 0.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                    }}
                  >
                    {[
                      {
                        label: 'Portfolio',
                        align: 'left',
                        sortKey: undefined as typeof sortCol | undefined,
                      },
                      {
                        label: 'Value',
                        align: 'right',
                        sortKey: 'value' as const,
                      },
                      { label: 'Qty', align: 'right', sortKey: 'qty' as const },
                      { label: 'Wt%', align: 'right', sortKey: 'wt' as const },
                      {
                        label: 'Share',
                        align: 'right',
                        sortKey: 'share' as const,
                      },
                    ].map((h) => (
                      <Typography
                        key={h.label}
                        variant='labelSmall'
                        onClick={
                          h.sortKey ? () => handleSort(h.sortKey!) : undefined
                        }
                        sx={{
                          ...SECTION_HEADER_SX,
                          textAlign: h.align as any,
                          cursor: h.sortKey ? 'pointer' : undefined,
                          userSelect: h.sortKey ? 'none' : undefined,
                          '&:hover': h.sortKey
                            ? { color: theme.color.onSurface }
                            : undefined,
                        }}
                      >
                        {h.label}
                        {h.sortKey && sortCol === h.sortKey && (
                          <Box component='span' sx={{ ...MICRO_SX, ml: 0.3 }}>
                            {sortDir === 'asc' ? '▲' : '▼'}
                          </Box>
                        )}
                      </Typography>
                    ))}
                  </Box>

                  <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                    {(() => {
                      const getSortVal = (p: (typeof sorted)[0]): number => {
                        if (sortCol === 'value') return p.valueCents;
                        if (sortCol === 'qty') return p.quantity;
                        if (sortCol === 'wt') return p.percentOfPortfolio;
                        if (sortCol === 'share')
                          return totalVal > 0 ? p.valueCents / totalVal : 0;
                        return p.valueCents;
                      };
                      const mult = sortDir === 'asc' ? 1 : -1;
                      const reSorted = [...displayPortfolios].sort(
                        (a, b) => mult * (getSortVal(a) - getSortVal(b)),
                      );
                      return reSorted;
                    })().map((p, idx) => {
                      const barPct = Math.max((p.valueCents / maxVal) * 100, 0);
                      const wtColor = weightColor(
                        theme.color,
                        p.percentOfPortfolio,
                      );
                      const isZero = p.valueCents === 0;
                      const sharePct = (p.valueCents / totalVal) * 100;
                      const isTopConcentration =
                        hasConcentration && sharePct > 50;
                      return (
                        <Box
                          key={p.portfolioId}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns:
                              'minmax(120px, 1.4fr) 100px 80px 60px 54px',
                            gap: 0,
                            alignItems: 'center',
                            py: 0.4,
                            px: 1,
                            borderBottom: '1px solid',
                            borderColor: theme.color.outlineVariant,
                            '&:last-child': { borderBottom: 'none' },

                            bgcolor:
                              idx % 2 === 0
                                ? 'transparent'
                                : theme.color.neutralHover,

                            opacity: isZero ? 0.4 : 1,

                            ...(isTopConcentration && {
                              borderLeft: '3px solid',
                              borderLeftColor: theme.color.warning,
                            }),
                          }}
                        >
                          <Box
                            sx={{
                              overflow: 'hidden',
                            }}
                          >
                            <Typography
                              variant='bodySmall'
                              sx={{ fontWeight: 600, ...TRUNCATE_SX }}
                            >
                              {p.portfolioName}
                            </Typography>
                            <Box
                              sx={{
                                mt: 0.2,
                                width: '100%',
                                height: 3,
                                bgcolor: theme.color.disabled,
                                borderRadius: 1,
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${barPct}%`,
                                  height: '100%',
                                  borderRadius: 1,
                                  bgcolor: isTopConcentration
                                    ? theme.color.warning
                                    : theme.color.primary,
                                  transition: 'width 0.3s',
                                }}
                              />
                            </Box>
                          </Box>
                          <Typography
                            variant='bodySmall'
                            sx={{
                              ...SMALL_BOLD_SX,
                              ...TABULAR_SX,
                              textAlign: 'right',
                            }}
                          >
                            {formatMoney(p.valueCents)}
                          </Typography>
                          <Typography
                            variant='bodySmall'
                            sx={{
                              ...SECONDARY_TEXT_SX,
                              ...TABULAR_SX,
                              textAlign: 'right',
                            }}
                          >
                            {p.quantity.toFixed(2)}
                          </Typography>
                          <Typography
                            variant='bodySmall'
                            sx={{
                              ...TABULAR_SX,
                              textAlign: 'right',
                              fontWeight: p.percentOfPortfolio > 15 ? 700 : 400,
                              color: wtColor,
                            }}
                          >
                            {p.percentOfPortfolio.toFixed(1)}%
                          </Typography>
                          <Typography
                            variant='bodySmall'
                            sx={{
                              ...TABULAR_SX,
                              textAlign: 'right',
                              color: theme.color.onDisabled,
                            }}
                          >
                            {sharePct.toFixed(1)}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  {zeroCount > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 0.5,
                        borderTop: '1px solid',
                        borderColor: theme.color.outlineVariant,
                      }}
                    >
                      <Typography
                        variant='labelSmall'
                        onClick={() => setShowAllPortfolios((v) => !v)}
                        sx={{
                          ...LABEL_SX,
                          cursor: 'pointer',
                          color: theme.color.primary,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {showAllPortfolios
                          ? `Hide ${zeroCount} empty`
                          : `Show all ${sorted.length} (${zeroCount} with $0)`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          );
        })()}
    </>
  );
};

const CrossPortfolioExposure = ({
  exposures,
  searchTerm,
  density = 'comfortable',
  onCreateTrades,
}: CrossPortfolioExposureProps) => {
  const formatMoney = useFormatMoney();
  const densityCfg = DENSITY_CONFIG[density];
  const theme = useTheme();
  const [groupByClass, setGroupByClass] = useState(false);
  const [selectedTickers, setSelectedTickers] = useState<Set<string>>(
    new Set(),
  );
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [tradeDirection, setTradeDirection] = useState<
    'SELL' | 'BUY' | 'REBALANCE'
  >('REBALANCE');
  const [buyPctPerAccount, setBuyPctPerAccount] = useState<string>('');
  const [excludedPortfolios, setExcludedPortfolios] = useState<Set<string>>(
    new Set(),
  );

  const handleToggleTicker = useCallback((ticker: string) => {
    setSelectedTickers((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  }, []);

  const handleSelectAllTickers = useCallback(() => {
    setSelectedTickers((prev) =>
      prev.size === exposures.length
        ? new Set()
        : new Set(exposures.map((e) => e.ticker)),
    );
  }, [exposures]);

  const handleClearTickers = useCallback(
    () => setSelectedTickers(new Set()),
    [],
  );

  const allSelectionsFromTickers = useMemo(() => {
    const selections: PortfolioSelection[] = [];
    Array.from(selectedTickers).forEach((ticker) => {
      const exp = exposures.find((e) => e.ticker === ticker);
      if (!exp) return;
      exp.portfolios.forEach((p) => {
        selections.push({
          ticker: exp.ticker,
          portfolioId: p.portfolioId,
          portfolioReportId: p.portfolioReportId,
          portfolioName: p.portfolioName,
          subAccountId: p.subAccountId,
          financialProductId: exp.financialProductId,
          currency: exp.currency,
          valueCents: p.valueCents,
          quantity: p.quantity,
          expectedPercentage: p.expectedPercentage,
          expectedValueCents: p.expectedValueCents,
          portfolioMarketValueCents: (p as any).portfolioMarketValueCents || 0,
          cashAvailableForTradeCents:
            (p as any).cashAvailableForTradeCents || 0,
          modelPortfolioType: (p as any).modelPortfolioType || 'SUB_ACCOUNT',
          objectId: (p as any).objectId || '',
        });
      });
    });
    return selections;
  }, [selectedTickers, exposures]);

  const activeSelections = useMemo(
    () =>
      allSelectionsFromTickers.filter(
        (s) => !excludedPortfolios.has(`${s.ticker}::${s.portfolioId}`),
      ),
    [allSelectionsFromTickers, excludedPortfolios],
  );

  const computedTrades = useMemo(() => {
    const buyPct = parseFloat(buyPctPerAccount) || 0;

    // First pass: compute raw amounts and accumulate sell proceeds + buy totals per portfolio
    const rawTrades = activeSelections.map((sel) => {
      let type: 'BUY' | 'SELL' | null = null;
      let amountCents = 0;
      if (tradeDirection === 'SELL') {
        type = 'SELL';
        amountCents = sel.valueCents;
      } else if (tradeDirection === 'BUY') {
        type = 'BUY';
        amountCents = Math.round(
          (sel.portfolioMarketValueCents * buyPct) / 100,
        );
      } else {
        const diff = sel.expectedValueCents - sel.valueCents;
        if (Math.abs(diff) < 100) {
          type = null;
          amountCents = 0;
        } else {
          type = diff > 0 ? 'BUY' : 'SELL';
          amountCents = Math.abs(diff);
        }
      }
      return { ...sel, type, amountCents };
    });

    // Second pass: cap buys per portfolio against available cash + sell proceeds
    const portfolioBuyTotals = new Map<
      string,
      { totalBuyCents: number; totalSellCents: number; cashCents: number }
    >();
    for (const t of rawTrades) {
      if (!t.type) continue;
      const stats = portfolioBuyTotals.get(t.portfolioId) ?? {
        totalBuyCents: 0,
        totalSellCents: 0,
        cashCents: t.cashAvailableForTradeCents,
      };
      if (t.type === 'BUY') stats.totalBuyCents += t.amountCents;
      else stats.totalSellCents += t.amountCents;
      portfolioBuyTotals.set(t.portfolioId, stats);
    }

    return rawTrades.map((t) => {
      if (t.type !== 'BUY' || t.amountCents === 0) return t;
      const stats = portfolioBuyTotals.get(t.portfolioId);
      if (!stats || stats.totalBuyCents === 0) return t;
      const buyingPower = stats.cashCents + stats.totalSellCents;
      if (stats.totalBuyCents <= buyingPower) return t;
      const scale = Math.max(0, buyingPower / stats.totalBuyCents);
      return {
        ...t,
        amountCents: Math.round(t.amountCents * scale),
        cashCapped: true,
      };
    });
  }, [activeSelections, tradeDirection, buyPctPerAccount]);

  const actionableTrades = computedTrades.filter(
    (t) => t.type && t.amountCents > 0,
  );
  const totalBuys = actionableTrades
    .filter((t) => t.type === 'BUY')
    .reduce((s, t) => s + t.amountCents, 0);
  const totalSells = actionableTrades
    .filter((t) => t.type === 'SELL')
    .reduce((s, t) => s + t.amountCents, 0);

  const cashCappedCount = useMemo(() => {
    const capped = new Set<string>();
    for (const t of computedTrades) {
      if ((t as any).cashCapped) capped.add(t.portfolioId);
    }
    return capped.size;
  }, [computedTrades]);

  const tradesByTicker = useMemo(() => {
    const map = new Map<string, typeof computedTrades>();
    computedTrades.forEach((t) => {
      if (!map.has(t.ticker)) map.set(t.ticker, []);
      map.get(t.ticker)!.push(t);
    });
    return map;
  }, [computedTrades]);

  const filteredExposures = searchTerm
    ? exposures.filter(
        (e) =>
          e.ticker.toLowerCase().includes(searchTerm.toLowerCase()) ||
          e.productName.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : exposures;

  const groupedExposures = useMemo(() => {
    if (!groupByClass) return null;
    const groups = new Map<string, AggregateExposure[]>();
    filteredExposures.forEach((e) => {
      const cls = getAssetClassName(e);
      if (!groups.has(cls)) groups.set(cls, []);
      groups.get(cls)!.push(e);
    });
    return Array.from(groups.entries())
      .sort(
        ([, a], [, b]) =>
          b.reduce((s, e) => s + e.totalValueCents, 0) -
          a.reduce((s, e) => s + e.totalValueCents, 0),
      )
      .map(([label, exposures]) => ({
        assetClass: label,
        label,
        exposures,
        totalValue: exposures.reduce((s, e) => s + e.totalValueCents, 0),
      }));
  }, [groupByClass, filteredExposures]);

  const handleToggleGroup = useCallback(
    (groupExposures: AggregateExposure[]) => {
      setSelectedTickers((prev) => {
        const groupTickers = groupExposures.map((e) => e.ticker);
        const allSelected = groupTickers.every((t) => prev.has(t));
        const next = new Set(prev);
        if (allSelected) groupTickers.forEach((t) => next.delete(t));
        else groupTickers.forEach((t) => next.add(t));
        return next;
      });
    },
    [],
  );

  const columns = useTickerColumns();

  const { table } = useDataTable({
    data: filteredExposures,
    columns,
    sortingMode: 'client',
  });

  const allTickersSelected =
    filteredExposures.length > 0 &&
    filteredExposures.every((e) => selectedTickers.has(e.ticker));
  const someTickersSelected = filteredExposures.some((e) =>
    selectedTickers.has(e.ticker),
  );

  const openReviewModal = () => {
    setExcludedPortfolios(new Set());
    setShowReviewModal(true);
  };

  return (
    <Box>
      {selectedTickers.size > 0 && (
        <Box
          sx={{
            mb: 1,
            px: 2,
            py: 1,
            bgcolor: theme.color.primaryVariant,
            border: '1px solid',
            borderColor: theme.color.primary,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
            <strong style={{ color: 'inherit' }}>{selectedTickers.size}</strong>{' '}
            ticker{selectedTickers.size !== 1 ? 's' : ''} selected
            {' · '}
            <strong>{allSelectionsFromTickers.length}</strong> account
            {allSelectionsFromTickers.length !== 1 ? 's' : ''} across selected
            tickers
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Button variant='text' size='small' onClick={handleClearTickers}>
              Clear
            </Button>
            <Button variant='contained' size='small' onClick={openReviewModal}>
              {`Trade ${selectedTickers.size} Ticker${selectedTickers.size !== 1 ? 's' : ''}`}
            </Button>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          mb: 0.5,
        }}
      >
        <Box
          onClick={() => setGroupByClass((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            px: 1,
            py: 0.4,
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: groupByClass
              ? theme.color.primary
              : theme.color.outlineVariant,
            bgcolor: groupByClass ? theme.color.primaryVariant : 'transparent',
            transition: 'all 0.12s',
            '&:hover': { borderColor: theme.color.primary },
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{
              ...SMALL_BOLD_SX,
              color: groupByClass
                ? theme.color.primary
                : theme.color.onSurfaceVariant,
            }}
          >
            Group by Asset Class
          </Typography>
        </Box>
      </Box>

      {groupByClass && groupedExposures ? (
        <Box>
          {groupedExposures.map((group) => {
            const groupTickers = group.exposures.map((e) => e.ticker);
            const allGroupSelected =
              groupTickers.length > 0 &&
              groupTickers.every((t) => selectedTickers.has(t));
            const someGroupSelected = groupTickers.some((t) =>
              selectedTickers.has(t),
            );
            return (
              <Box key={group.assetClass} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    bgcolor: theme.color.neutralHover,
                    borderBottom: '2px solid',
                    borderColor: theme.color.primary,
                    borderRadius: '4px 4px 0 0',
                  }}
                >
                  <CheckboxField
                    checked={allGroupSelected}
                    indeterminate={someGroupSelected && !allGroupSelected}
                    onChange={() => handleToggleGroup(group.exposures)}
                    customStyle={{ transform: 'scale(0.8)' }}
                  />

                  <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
                    {group.label}
                  </Typography>
                  <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                    {group.exposures.length} ticker
                    {group.exposures.length !== 1 ? 's' : ''}
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...SECONDARY_TEXT_SX,
                      ...TABULAR_SX,
                      ml: 'auto',
                    }}
                  >
                    {formatMoney(group.totalValue)}
                  </Typography>
                </Box>
                <Table sx={{ ...ALIGNED_TABLE_SX }}>
                  <PortfolioTradingTableHead
                    table={table}
                    skipColumnIds={PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS.ticker}
                    rowSx={{ bgcolor: theme.color.background }}
                    leadingCells={[
                      { key: 'select-spacer', sx: { width: 28 } },
                      { key: 'expand-spacer', sx: { width: 28 } },
                    ]}
                  />

                  <TableBody>
                    {group.exposures.map((exp) => (
                      <ExposureRow
                        key={exp.ticker}
                        exposure={exp}
                        isSelected={selectedTickers.has(exp.ticker)}
                        onToggleTicker={handleToggleTicker}
                        densityCfg={densityCfg}
                        theme={theme}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            );
          })}
        </Box>
      ) : (
        <>
          <Table sx={{ ...ALIGNED_TABLE_SX }}>
            <PortfolioTradingTableHead
              table={table}
              skipColumnIds={PORTFOLIO_TRADING_PHANTOM_COLUMN_IDS.ticker}
              rowSx={{ bgcolor: 'background.default' }}
              leadingCells={[
                {
                  key: 'select',
                  sx: { width: 40 },
                  content: (
                    <CheckboxField
                      checked={allTickersSelected}
                      indeterminate={someTickersSelected && !allTickersSelected}
                      onChange={handleSelectAllTickers}
                      customStyle={{ transform: 'scale(0.8)' }}
                    />
                  ),
                },
                { key: 'expand', sx: { width: 40 } },
              ]}
            />

            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <ExposureRow
                  key={row.original.ticker}
                  exposure={row.original}
                  isSelected={selectedTickers.has(row.original.ticker)}
                  onToggleTicker={handleToggleTicker}
                  densityCfg={densityCfg}
                  theme={theme}
                />
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {filteredExposures.length === 0 && (
        <Box
          sx={{
            py: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
            {searchTerm
              ? 'No securities match your search'
              : 'No holdings data available'}
          </Typography>
        </Box>
      )}

      <Dialog
        open={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        maxWidth={false}
        // ovComponents Dialog does not expose paperProps — MUI class selector required for sizing
        sx={{ '.MuiDialog-paper': { width: 520, maxHeight: '80vh' } }}
      >
        <DialogTitle onClose={() => setShowReviewModal(false)}>
          <Box>
            <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
              Generate Trades
            </Typography>
            <Typography
              variant='labelSmall'
              component='div'
              sx={{ ...SECONDARY_TEXT_SX, mt: 0.25 }}
            >
              {selectedTickers.size} ticker
              {selectedTickers.size !== 1 ? 's' : ''}
              {' · '}
              {activeSelections.length} account
              {activeSelections.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: '0 !important' }}>
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderBottom: '1px solid',
              borderColor: theme.color.outlineVariant,
            }}
          >
            {DIRECTION_CHIPS.map(({ dir, colorKey }) => {
              const isActive = tradeDirection === dir;
              const accentColor = theme.color[colorKey];
              const accentVariant = theme.color[`${colorKey}Variant`];
              return (
                <Box
                  key={dir}
                  onClick={() => setTradeDirection(dir)}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1.5px solid',
                    borderColor: isActive
                      ? accentColor
                      : theme.color.outlineVariant,
                    bgcolor: isActive ? accentVariant : 'transparent',
                    transition: 'all 0.12s',
                    '&:hover': {
                      borderColor: accentColor,
                      bgcolor: accentVariant,
                    },
                  }}
                >
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...BOLD_SX,
                      ...VALUE_SX,
                      letterSpacing: '0.02em',
                      color: isActive
                        ? accentColor
                        : theme.color.onSurfaceVariant,
                    }}
                  >
                    {dir}
                  </Typography>
                </Box>
              );
            })}
            {tradeDirection === 'BUY' && (
              <TextField
                label=''
                placeholder='% of portfolio'
                value={buyPctPerAccount}
                onChange={(e: any) => setBuyPctPerAccount(e.target.value)}
                size='small'
                sx={{
                  width: 120,
                  ...BLOTTER_INPUT_SX,
                  '& .MuiInputBase-input': { fontSize: '0.7rem' },
                }}
              />
            )}
            <Typography
              variant='labelSmall'
              sx={{ ...SECONDARY_TEXT_SX, ...SMALL_SX, ml: 'auto' }}
            >
              {tradeDirection === 'SELL'
                ? 'Full liquidation'
                : tradeDirection === 'BUY'
                  ? "Percentage of each portfolio's value"
                  : 'Rebalance to target weight'}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 1 }}>
            {Array.from(tradesByTicker.entries()).map(([ticker, trades]) => {
              const visibleTrades = trades.filter(
                (t) => t.type && t.amountCents > 0,
              );
              if (visibleTrades.length === 0) return null;
              return (
                <Box key={ticker} sx={{ mb: 1.5 }}>
                  <Typography
                    variant='labelSmall'
                    component='div'
                    sx={{
                      ...LABEL_SX,
                      ...SECTION_HEADER_SX,
                      fontWeight: 800,
                      mb: 0.25,
                    }}
                  >
                    {ticker}
                  </Typography>
                  {visibleTrades.map((t) => {
                    const isExcluded = excludedPortfolios.has(
                      `${t.ticker}::${t.portfolioId}`,
                    );
                    return (
                      <Box
                        key={t.portfolioId}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 70px 50px 70px 20px',
                          gap: 0.5,
                          alignItems: 'center',
                          py: 0.35,
                          px: 0.5,
                          borderBottom: '1px solid',
                          borderColor: theme.color.outlineVariant,
                          '&:last-child': { borderBottom: 'none' },
                          borderLeft: '2px solid',

                          borderLeftColor: isExcluded
                            ? theme.color.outlineVariant
                            : t.type
                              ? tradeSideColor(theme.color, t.type === 'BUY')
                              : theme.color.outlineVariant,

                          opacity: isExcluded ? 0.4 : t.type ? 1 : 0.5,
                          textDecoration: isExcluded ? 'line-through' : 'none',
                        }}
                      >
                        <Typography
                          variant='labelSmall'
                          sx={{
                            ...LABEL_SX,
                            ...TRUNCATE_SX,
                          }}
                        >
                          {t.portfolioName}
                        </Typography>
                        <Typography
                          variant='labelSmall'
                          sx={{
                            ...SECONDARY_TEXT_SX,
                            ...SMALL_SX,
                            textAlign: 'right',
                          }}
                        >
                          {formatMoney(t.valueCents)}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          {!isExcluded && t.type ? (
                            <Typography
                              variant='labelSmall'
                              sx={{
                                ...MICRO_SX,
                                fontWeight: 800,
                                color:
                                  t.type === 'BUY'
                                    ? theme.color.positive
                                    : theme.color.negative,
                              }}
                            >
                              {t.type}
                            </Typography>
                          ) : (
                            <Typography
                              variant='labelSmall'
                              sx={{
                                ...MICRO_SX,
                                color: theme.color.onDisabled,
                              }}
                            >
                              {isExcluded ? '—' : 'AT TGT'}
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          variant='labelSmall'
                          sx={{
                            ...LABEL_SX,
                            ...TABULAR_SX,
                            textAlign: 'right',
                            color: isExcluded
                              ? theme.color.onDisabled
                              : t.type
                                ? tradeSideColor(theme.color, t.type === 'BUY')
                                : theme.color.onDisabled,
                          }}
                        >
                          {!isExcluded && t.amountCents > 0
                            ? formatMoney(t.amountCents)
                            : '—'}
                        </Typography>
                        <Typography
                          variant='labelSmall'
                          onClick={() => {
                            setExcludedPortfolios((prev) => {
                              const next = new Set(prev);
                              const key = `${t.ticker}::${t.portfolioId}`;
                              if (next.has(key)) next.delete(key);
                              else next.add(key);
                              return next;
                            });
                          }}
                          sx={{
                            ...VALUE_SX,
                            cursor: 'pointer',
                            textAlign: 'center',
                            color: isExcluded
                              ? theme.color.primary
                              : theme.color.onDisabled,
                            '&:hover': {
                              color: isExcluded
                                ? theme.color.primary
                                : theme.color.negative,
                            },
                            lineHeight: 1,
                          }}
                        >
                          {isExcluded ? '↩' : '✕'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </DialogContent>

        <DialogFooter>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mr: 'auto',
            }}
          >
            {[
              {
                label: 'BUYS',
                value: formatMoney(totalBuys),
                color: theme.color.positive,
                tabular: true,
              },
              {
                label: 'SELLS',
                value: formatMoney(totalSells),
                color: theme.color.negative,
                tabular: true,
              },
              { label: 'TRADES', value: actionableTrades.length },
              ...(cashCappedCount > 0
                ? [
                    {
                      label: 'CASH-CAPPED',
                      value: `${cashCappedCount} portfolio${cashCappedCount !== 1 ? 's' : ''}`,
                      color: theme.color.warning,
                    },
                  ]
                : []),
              ...(excludedPortfolios.size > 0
                ? [
                    {
                      label: 'EXCLUDED',
                      value: excludedPortfolios.size,
                      color: theme.color.onSurfaceVariant,
                    },
                  ]
                : []),
            ].map((s) => (
              <Box key={s.label}>
                <Typography
                  variant='labelSmall'
                  component='div'
                  sx={{ ...SECONDARY_TEXT_SX, ...MICRO_SX }}
                >
                  {s.label}
                </Typography>
                <Typography
                  variant='bodySmall'
                  component='div'
                  sx={{
                    fontWeight: 600,
                    ...(s.tabular ? TABULAR_SX : {}),
                    color: s.color ?? theme.color.onSurface,
                  }}
                >
                  {s.value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Button
            variant='text'
            size='small'
            onClick={() => setShowReviewModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            size='small'
            disabled={actionableTrades.length === 0}
            onClick={() => {
              if (onCreateTrades) {
                const buyAmt =
                  tradeDirection === 'BUY'
                    ? parseFloat(buyPctPerAccount) || 0
                    : undefined;
                onCreateTrades(activeSelections, tradeDirection, buyAmt);
                handleClearTickers();
                setShowReviewModal(false);
              }
            }}
          >
            {`Confirm ${actionableTrades.length} Trade${actionableTrades.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </Dialog>
    </Box>
  );
};

export default CrossPortfolioExposure;
